import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, ChevronLeft, LayoutGrid } from "lucide-react";
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { ContextActionsMenu } from "./ContextActionsMenu";
import { RESTORE_SCROLL_STATE } from "./origin";
import { flattenItems } from "./paths";
import { useContextNav } from "./useContextNav";
import type { MatchedContext, NavItem } from "./types";

interface ContextNavigationProps {
  matched: MatchedContext;
  /** Shows the main navigation inside the sidebar (L7). */
  onShowMainMenu: () => void;
  onNavigate: () => void;
}

function ItemBadge({ badge }: { badge: NavItem["badge"] }) {
  if (badge === null || badge === undefined || badge === "" || badge === 0) {
    return null;
  }
  return <SidebarMenuBadge>{badge}</SidebarMenuBadge>;
}

function SubItems({
  items,
  activeKeys,
  onNavigate,
  depth,
}: {
  items: NavItem[];
  activeKeys: Set<string>;
  onNavigate: () => void;
  depth: number;
}) {
  return (
    <SidebarMenuSub className={cn(depth > 1 && "mr-0 pr-0")}>
      {items.map((item) => {
        const isActive = activeKeys.has(item.key);
        return (
          <SidebarMenuSubItem key={item.key}>
            <SidebarMenuSubButton
              asChild
              isActive={isActive}
              className={cn(isActive && "text-primary font-medium")}
            >
              <Link
                to={item.href}
                onClick={onNavigate}
                data-testid={item.testId}
                aria-current={isActive ? "page" : undefined}
              >
                {item.icon && <item.icon className="h-4 w-4" />}
                <span className="truncate">{item.label}</span>
                {item.badge ? (
                  <span className="ml-auto text-xs text-muted-foreground">
                    {item.badge}
                  </span>
                ) : null}
              </Link>
            </SidebarMenuSubButton>
            {item.children?.length && isActive ? (
              <SubItems
                items={item.children}
                activeKeys={activeKeys}
                onNavigate={onNavigate}
                depth={depth + 1}
              />
            ) : null}
          </SidebarMenuSubItem>
        );
      })}
    </SidebarMenuSub>
  );
}

function SectionItem({
  item,
  activeKeys,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  activeKeys: Set<string>;
  collapsed: boolean;
  onNavigate: () => void;
}) {
  const isActive = activeKeys.has(item.key);
  const hasChildren = !!item.children?.length;
  const [open, setOpen] = useState(isActive);
  useEffect(() => {
    if (isActive) setOpen(true);
  }, [isActive]);
  const Icon = item.icon;

  // Icon rail: sub-sections in a flyout (L8).
  if (collapsed && hasChildren) {
    return (
      <SidebarMenuItem>
        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  isActive={isActive}
                  data-testid={item.testId}
                  aria-label={item.label}
                >
                  {Icon && <Icon className="h-4 w-4" />}
                  <span>{item.label}</span>
                </SidebarMenuButton>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="right">{item.label}</TooltipContent>
          </Tooltip>
          <DropdownMenuContent side="right" align="start">
            <DropdownMenuLabel>{item.label}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {flattenItems(item.children!).map((child) => (
              <DropdownMenuItem key={child.key} asChild>
                <Link
                  to={child.href}
                  className={cn(activeKeys.has(child.key) && "font-medium")}
                >
                  {child.label}
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    );
  }

  return (
    <Collapsible open={hasChildren && open} onOpenChange={setOpen}>
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          isActive={isActive && !hasChildren}
          tooltip={item.label}
          className={cn(
            isActive && "font-medium",
            isActive && !hasChildren && "text-primary",
          )}
        >
          <Link
            to={item.href}
            onClick={() => {
              if (hasChildren) setOpen(true);
              onNavigate();
            }}
            data-testid={item.testId}
            aria-current={isActive && !hasChildren ? "page" : undefined}
          >
            {Icon && <Icon className="h-4 w-4" />}
            <span className="truncate">{item.label}</span>
          </Link>
        </SidebarMenuButton>
        {hasChildren ? (
          <button
            type="button"
            aria-label={
              open ? `Collapse ${item.label}` : `Expand ${item.label}`
            }
            onClick={() => setOpen((value) => !value)}
            className="absolute right-1 top-1.5 flex h-5 w-5 items-center justify-center rounded-md text-sidebar-foreground/70 hover:bg-sidebar-accent group-data-[collapsible=icon]:hidden"
          >
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 transition-transform",
                !open && "-rotate-90",
              )}
            />
          </button>
        ) : (
          <ItemBadge badge={item.badge} />
        )}
        {hasChildren && (
          <CollapsibleContent>
            <SubItems
              items={item.children!}
              activeKeys={activeKeys}
              onNavigate={onNavigate}
              depth={1}
            />
          </CollapsibleContent>
        )}
      </SidebarMenuItem>
    </Collapsible>
  );
}

/**
 * The sidebar while the main view is inside a context (L2): up link, entity
 * header with switcher, sections with nested sub-sections.
 */
export function ContextNavigation({
  matched,
  onShowMainMenu,
  onNavigate,
}: ContextNavigationProps) {
  const location = useLocation();
  const { state, isMobile } = useSidebar();
  const collapsed = state === "collapsed" && !isMobile;
  const { header, items, activeChain, up } = useContextNav(
    matched,
    location.pathname,
  );
  const activeKeys = new Set(activeChain.map((item) => item.key));
  const { def, params } = matched;
  const Switcher = def.Switcher;
  const Body = def.Body;
  const TypeIcon = def.icon;

  return (
    <>
      <SidebarHeader className="border-b border-sidebar-border gap-2 p-2">
        <div className="flex items-center gap-1">
          <SidebarMenu className="flex-1 min-w-0">
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                tooltip={`Back to ${up.label}`}
                className="text-muted-foreground hover:text-foreground"
              >
                <Link
                  to={up.href}
                  state={up.isOrigin ? RESTORE_SCROLL_STATE : undefined}
                  data-testid="context-up-link"
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span className="truncate">{up.label}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
          {!collapsed && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0 text-muted-foreground"
              title="Main menu"
              aria-label="Main menu"
              onClick={onShowMainMenu}
              data-testid="show-main-menu"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
          )}
        </div>
        {collapsed ? (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex h-8 w-8 items-center justify-center rounded-md bg-sidebar-accent">
                  <TypeIcon className="h-4 w-4" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="right">
                {header.title ?? def.typeLabel}
              </TooltipContent>
            </Tooltip>
            <ContextActionsMenu className="h-8 w-8" />
          </>
        ) : (
          <div className="px-2 pb-1 min-w-0" data-testid="context-header">
            <div className="flex items-center gap-1 min-h-5">
              <span className="text-xs text-muted-foreground font-mono truncate flex-1">
                {header.title === undefined ? (
                  <Skeleton className="h-3 w-16" />
                ) : (
                  (header.kicker ?? def.typeLabel)
                )}
              </span>
              {Switcher && <Switcher params={params} />}
            </div>
            <div className="flex items-start gap-1">
              {header.title === undefined ? (
                <Skeleton className="mt-1 h-5 w-40" />
              ) : (
                <div
                  className="flex-1 min-w-0 text-sm font-semibold leading-tight text-sidebar-foreground break-words"
                  data-testid="context-title"
                >
                  {header.title}
                </div>
              )}
              <ContextActionsMenu className="-mr-1 -mt-1 ml-auto" />
            </div>
            {header.status && <div className="mt-1.5">{header.status}</div>}
          </div>
        )}
      </SidebarHeader>
      <SidebarContent className="scrollbar-thin">
        {Body ? (
          <Body params={params} collapsed={collapsed} />
        ) : (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu data-testid="context-sections">
                {items.map((item) => (
                  <SectionItem
                    key={item.key}
                    item={item}
                    activeKeys={activeKeys}
                    collapsed={collapsed}
                    onNavigate={onNavigate}
                  />
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>
    </>
  );
}
